import { Box, ClickAwayListener, Popper } from '@mui/material'
import { t } from 'i18next'
import { ReactNode, useEffect, useRef, useState } from 'react'

interface PricingTooltipProps {
  children: ReactNode
  title: ReactNode
  image?: string
  link?: string
  linkText?: string
}

export default function PricingTooltip(props: PricingTooltipProps) {
  const [open, setOpen] = useState(false)
  const [anchorElement, setAnchorElement] = useState<HTMLSpanElement | null>(
    null
  )
  const closeTimeoutRef = useRef<number | null>(null)
  const isAnchorHoveredRef = useRef(false)
  const isTooltipHoveredRef = useRef(false)
  const isFocusedRef = useRef(false)

  const cancelClose = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const scheduleClose = () => {
    cancelClose()
    closeTimeoutRef.current = window.setTimeout(() => {
      if (
        !isAnchorHoveredRef.current &&
        !isTooltipHoveredRef.current &&
        !isFocusedRef.current
      ) {
        setOpen(false)
      }
    }, 180)
  }

  useEffect(() => {
    return () => cancelClose()
  }, [])

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box className="pricing-tooltip-trigger">
        <Box
          ref={setAnchorElement}
          component="span"
          tabIndex={0}
          role="button"
          aria-expanded={open}
          aria-haspopup="dialog"
          onMouseEnter={() => {
            isAnchorHoveredRef.current = true
            cancelClose()
            setOpen(true)
          }}
          onMouseLeave={() => {
            isAnchorHoveredRef.current = false
            scheduleClose()
          }}
          onFocus={() => {
            isFocusedRef.current = true
            cancelClose()
            setOpen(true)
          }}
          onBlur={() => {
            isFocusedRef.current = false
            scheduleClose()
          }}
          onClick={(event) => {
            event.stopPropagation()
            cancelClose()
            setOpen((isOpen) => !isOpen)
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return

            event.preventDefault()
            event.stopPropagation()
            cancelClose()
            setOpen((isOpen) => !isOpen)
          }}
        >
          {props.children}
        </Box>
        <Popper
          open={open && Boolean(anchorElement)}
          anchorEl={anchorElement}
          placement="right-start"
          sx={{ zIndex: 10000000 }}
          modifiers={[
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['left-start', 'bottom-start', 'top-start']
              }
            },
            {
              name: 'preventOverflow',
              options: { padding: 16 }
            }
          ]}
        >
          <Box
            className="pricing-tooltip-surface"
            role="dialog"
            onMouseEnter={() => {
              isTooltipHoveredRef.current = true
              cancelClose()
            }}
            onMouseLeave={() => {
              isTooltipHoveredRef.current = false
              scheduleClose()
            }}
          >
            <Box className="pricing-tooltip-text">{props.title}</Box>
            {props.image && (
              <Box className="pricing-tooltip-image-container">
                <img
                  className="pricing-tooltip-image"
                  src={props.image}
                  alt={props.linkText ?? ''}
                />
              </Box>
            )}
            {props.link && (
              <Box className="pricing-tooltip-link-container">
                <Box
                  component="a"
                  href={props.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pricing-tooltip-link"
                >
                  {t('readMore')} - {props.linkText}
                </Box>
              </Box>
            )}
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}
